import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService, cursosService } from '../services/api';
import Pagination from '../components/Pagination';
import ConfirmPopup from '../components/ConfirmPopup';
import './Cursos.css';

const ITEMS_PER_PAGE = 15;

const Cursos = () => {
  const navigate = useNavigate();
  const [cursos, setCursos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentUser, setCurrentUser] = useState(null);
  const [selectedRole, setSelectedRole] = useState(null);
  const [isProfesor, setIsProfesor] = useState(false);
  const [isTutor, setIsTutor] = useState(false);
  const [isCoordinador, setIsCoordinador] = useState(false);
  const [isEquipo, setIsEquipo] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedEquipoRows, setExpandedEquipoRows] = useState({});
  const [confirmEliminar, setConfirmEliminar] = useState({ show: false, curso: null });
  const [errorModal, setErrorModal] = useState('');

  const filteredCursos = useMemo(() => {
    const term = (searchTerm || '').trim().toLowerCase();
    if (!term) return cursos;
    return cursos.filter((c) => {
      const nombre = (c.nombre || '').toLowerCase();
      const codigo = (c.codigo || '').toLowerCase();
      return nombre.includes(term) || codigo.includes(term);
    });
  }, [cursos, searchTerm]);

  const totalPages = Math.max(1, Math.ceil(filteredCursos.length / ITEMS_PER_PAGE));
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedCursos = useMemo(
    () => filteredCursos.slice(startIndex, startIndex + ITEMS_PER_PAGE),
    [filteredCursos, startIndex]
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  useEffect(() => {
    // Verificar autenticación
    if (!authService.isAuthenticated()) {
      navigate('/login');
      return;
    }

    // Obtener usuario actual
    const user = authService.getCurrentUser();
    setCurrentUser(user);
    
    // Obtener el rol seleccionado desde localStorage
    const savedRole = localStorage.getItem('selectedRole');
    if (savedRole) {
      setSelectedRole(savedRole);
      setIsProfesor(savedRole === 'Profesor');
      setIsTutor(savedRole === 'Tutor');
      setIsCoordinador(savedRole === 'Coordinador');
      setIsEquipo(savedRole === 'Equipo');
    } else if (user && user.groups) {
      const grupos = Array.isArray(user.groups) ? user.groups : [user.groups];
      const primerRol = typeof grupos[0] === 'string' ? grupos[0] : (grupos[0].name || String(grupos[0]));
      setSelectedRole(primerRol);
      setIsProfesor(primerRol === 'Profesor');
      setIsTutor(primerRol === 'Tutor');
      setIsCoordinador(primerRol === 'Coordinador');
      setIsEquipo(primerRol === 'Equipo');
      localStorage.setItem('selectedRole', primerRol);
    }

    loadCursos();
  }, [navigate]);

  // Escuchar cambios en el rol seleccionado
  useEffect(() => {
    const handleRoleChange = (event) => {
      const newRole = event.detail?.role || localStorage.getItem('selectedRole');
      if (newRole && newRole !== selectedRole) {
        setSelectedRole(newRole);
        setIsProfesor(newRole === 'Profesor');
        setIsTutor(newRole === 'Tutor');
        setIsCoordinador(newRole === 'Coordinador');
        setIsEquipo(newRole === 'Equipo');
      }
    };

    // Escuchar evento personalizado de cambio de rol
    window.addEventListener('roleChanged', handleRoleChange);
    
    // También escuchar cambios en localStorage (para cambios desde otras pestañas)
    window.addEventListener('storage', handleRoleChange);

    return () => {
      window.removeEventListener('roleChanged', handleRoleChange);
      window.removeEventListener('storage', handleRoleChange);
    };
  }, [selectedRole]);

  // Cargar cursos cuando cambien isProfesor, isCoordinador o currentUser
  useEffect(() => {
    if (currentUser !== null) {
      loadCursos();
    }
  }, [isProfesor, isTutor, isCoordinador, currentUser]);

  const loadCursos = async () => {
    try {
      setLoading(true);
      const data = await cursosService.getCursos();
      
      // Si es Profesor o Tutor (y no Coordinador), filtrar solo los cursos donde está asignado en el equipo
      if ((isProfesor || isTutor) && !isCoordinador && currentUser) {
        const cursosFiltrados = data.filter(curso => {
          if (curso.equipo && Array.isArray(curso.equipo)) {
            return curso.equipo.some(m => {
              const id = typeof m === 'object' && m && (m.id != null || m.user_id != null) ? (m.user_id ?? m.id) : m;
              return parseInt(id) === parseInt(currentUser.id);
            });
          }
          return false;
        });
        setCursos(cursosFiltrados);
      } else {
        // Coordinador o administradores ven todos los cursos
        setCursos(data);
      }
    } catch (err) {
      const msg = err.response?.data?.error || 'Error al cargar cursos';
      setError(msg);
      setCursos([]);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  /** Rol del usuario actual en un curso (desde curso.equipo). Devuelve null si no está en el equipo. */
  const getMiRolEnCurso = (curso) => {
    if (!currentUser || !curso || !curso.equipo || !Array.isArray(curso.equipo)) return null;
    const miembro = curso.equipo.find(m => {
      const id = typeof m === 'object' && m && (m.id != null || m.user_id != null) ? (m.user_id ?? m.id) : m;
      return parseInt(id) === parseInt(currentUser.id);
    });
    return miembro && typeof miembro === 'object' && miembro.rol ? miembro.rol : (miembro ? 'Profesor' : null);
  };

  /** true si el usuario puede editar este curso (coordinador, o Profesor/Tutor asignado a este curso). */
  const puedoEditarCurso = (curso) => {
    if (isCoordinador) return true;
    const miRol = getMiRolEnCurso(curso);
    return miRol === 'Profesor' || miRol === 'Tutor';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const raw = String(dateString).trim();
    const onlyDate = raw.slice(0, 10);
    const [y, m, d] = onlyDate.split('-').map(Number);
    if ([y, m, d].some(Number.isNaN)) {
      return raw || '-';
    }
    // Parse local para evitar corrimiento por UTC.
    return new Date(y, m - 1, d).toLocaleDateString('es-AR');
  };

  const toggleEquipoRow = (cursoId) => {
    setExpandedEquipoRows((prev) => ({
      ...prev,
      [cursoId]: !prev[cursoId],
    }));
  };

  const handleEliminar = (curso) => {
    setConfirmEliminar({ show: true, curso });
  };

  const confirmarEliminarCurso = async () => {
    const curso = confirmEliminar.curso;
    setConfirmEliminar({ show: false, curso: null });
    if (!curso?.id) return;
    try {
      await cursosService.deleteCurso(curso.id);
      setCursos((prev) => prev.filter((c) => c.id !== curso.id));
    } catch (err) {
      const data = err.response?.data || {};
      const n = data.alumnos_inscriptos;
      if (typeof n === 'number' && n > 0) {
        setErrorModal(
          n === 1
            ? 'No se puede eliminar el curso porque hay 1 alumno inscripto.'
            : `No se puede eliminar el curso porque hay ${n} alumnos inscriptos.`
        );
      } else {
        setErrorModal(data.error || 'Error al eliminar el curso');
      }
      console.error(err);
    }
  };

  if (loading) {
    return (
      <div className="cursos-container">
        <div className="cursos-loading">
          <div className="loading-spinner"></div>
          <p>Cargando cursos...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="cursos-container">
      <div className="cursos-header">
        <h1>Gestión de Cursos</h1>
        {isCoordinador && (
          <button
            className="cursos-btn cursos-btn-primary"
            onClick={() => navigate('/dashboard/cursos/nuevo')}
          >
            ➕ Nuevo Curso
          </button>
        )}
      </div>

      <ConfirmPopup
        open={Boolean(error)}
        title="Error"
        message={error}
        confirmLabel="Aceptar"
        cancelLabel={null}
        onConfirm={() => setError('')}
        onCancel={() => setError('')}
        variant="danger"
      />

      {cursos.length > 0 && (
        <div className="cursos-filtro-busqueda">
          <label htmlFor="cursos-busqueda" className="cursos-filtro-label">Buscar por nombre o código</label>
          <input
            id="cursos-busqueda"
            type="text"
            className="cursos-input-busqueda"
            placeholder="Nombre o código del curso..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      )}

      <div className="cursos-table-container">
        <table className="cursos-table">
          <thead>
            <tr>
              <th>Código</th>
              <th>Nombre</th>
              <th>Duración (hs)</th>
              <th>Fecha Inicio</th>
              <th>Fecha Fin</th>
              <th>Cupo</th>
              <th>Equipo</th>
              <th>Estado</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {filteredCursos.length === 0 ? (
              <tr>
                <td colSpan="9" className="cursos-empty">
                  {cursos.length === 0 ? 'No hay cursos registrados' : 'No hay cursos que coincidan con la búsqueda'}
                </td>
              </tr>
            ) : (
              paginatedCursos.map((curso) => (
                <tr key={curso.id} className={curso.mostrar_en_home ? 'cursos-row-mostrar-home' : ''}>
                  <td>{curso.codigo || '-'}</td>
                  <td>{curso.nombre || '-'}</td>
                  <td>{curso.duracion_horas || 0}</td>
                  <td>{formatDate(curso.fecha_inicio)}</td>
                  <td>{formatDate(curso.fecha_fin)}</td>
                  <td>
                    {curso.cupo_maximo > 0 
                      ? `${curso.cupo_actual || 0}/${curso.cupo_maximo}`
                      : 'Sin límite'
                    }
                  </td>
                  <td>
                    {curso.equipo_nombres && curso.equipo_nombres.length > 0 ? (
                      <div className="cursos-equipo">
                        {(expandedEquipoRows[curso.id] ? curso.equipo_nombres : [curso.equipo_nombres[0]]).map((nombre, index) => (
                          <span key={index} className="cursos-equipo-badge">
                            {nombre}
                          </span>
                        ))}
                        {curso.equipo_nombres.length > 1 && (
                          <button
                            type="button"
                            className="cursos-equipo-ver-mas"
                            onClick={() => toggleEquipoRow(curso.id)}
                          >
                            {expandedEquipoRows[curso.id] ? 'Ver menos' : `Ver más (${curso.equipo_nombres.length - 1})`}
                          </button>
                        )}
                      </div>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td>
                    <span className={`cursos-status ${curso.activo ? 'active' : 'inactive'}`}>
                      {curso.activo ? 'Activo' : 'Inactivo'}
                    </span>
                  </td>
                  <td>
                    <div className="cursos-actions-cell">
                      {puedoEditarCurso(curso) ? (
                        <button
                          className="cursos-btn-action cursos-btn-edit"
                          onClick={() => navigate(`/dashboard/cursos/${curso.id}/editar`)}
                          title="Editar"
                        >
                          ✏️
                        </button>
                      ) : (
                        <span className="cursos-no-edit" title="Solo el Coordinador o el Profesor/Tutor asignado pueden editar">
                          —
                        </span>
                      )}
                      {isCoordinador && (
                        <button
                          type="button"
                          className="cursos-btn-action cursos-btn-delete"
                          onClick={() => handleEliminar(curso)}
                          title="Eliminar"
                        >
                          🗑️
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {filteredCursos.length > ITEMS_PER_PAGE && (
        <div className="cursos-pagination-wrap">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        </div>
      )}

      <ConfirmPopup
        open={confirmEliminar.show}
        title="Eliminar curso"
        message={
          confirmEliminar.curso?.nombre
            ? `¿Estás seguro de que deseás eliminar el curso «${confirmEliminar.curso.nombre}»? Esta acción no se puede deshacer.`
            : '¿Estás seguro de que deseás eliminar este curso? Esta acción no se puede deshacer.'
        }
        confirmLabel="Eliminar"
        cancelLabel="Cancelar"
        variant="danger"
        onConfirm={confirmarEliminarCurso}
        onCancel={() => setConfirmEliminar({ show: false, curso: null })}
      />
      <ConfirmPopup
        open={Boolean(errorModal)}
        title="No se puede eliminar"
        message={errorModal}
        confirmLabel="Aceptar"
        cancelLabel={null}
        variant="danger"
        onConfirm={() => setErrorModal('')}
        onCancel={() => setErrorModal('')}
      />
    </div>
  );
};

export default Cursos;
